var m = require('mithril');
var prop = require('mithril/stream');
var loaders = require('../layout/components/loaders');
var pickerRequestState = require('../layout/components/pickerRequestState');
var verticalListItem = require('../components/verticalListItem');
var pagination = require('../data/components/pagination');

// Tree navigator for selecting equipment by default, or locations via selectionType.
// API traversal mirrors treeLocationAndEquipment while filtering selectable rows.
var treeEquipment = {};
var nextTreeId = 1;

treeEquipment.oninit = function (vnode) {
  var ctrl = this;
  ctrl.attrs = vnode.attrs || {};
  ctrl.instanceId = nextTreeId++;
  ctrl.selectedNode = vnode.attrs.selectedNode; // stream for selected equipment record
  ctrl.startView = false;
  ctrl.rootRid = null; // root rid for the preloaded tree
  ctrl.treeRoot = null; // full tree root node
  ctrl.treePromise = null; // tracks initial load
  ctrl.nodeMap = new Map(); // rid -> node
  ctrl.parentMap = new Map(); // rid -> parent rid
  ctrl.appliedInitialRid = null;
  ctrl.currentNode = null; // pointer to node being displayed
  ctrl.descendantSearchData = null; // flattened list for search
  ctrl.searchResults = prop([]);
  ctrl.searchTerm = prop('');
  ctrl.searching = prop(false);
  ctrl.searchError = prop('');
  ctrl.searchRequestId = 0;
  ctrl.searchRenderData = [];
  ctrl.reinitializePagination = prop(false);
  ctrl.navRenderData = [];
  ctrl.reinitializeNavPagination = prop(false);
  ctrl.searchActive = prop(false);
  ctrl.searchWrapperId = 'tree-equipment-search-' + ctrl.instanceId;
  ctrl.searchInputId = 'tree-equipment-search-input-' + ctrl.instanceId;
  pickerRequestState.initialize(ctrl);
  ctrl.selectionType = function () {
    return (ctrl.attrs && ctrl.attrs.selectionType) || 'equipment';
  };
  ctrl.selectionName = function () {
    return ctrl.selectionType() === 'location' ? 'location' : 'equipment';
  };
  ctrl.selectionPluralName = function () {
    return ctrl.selectionType() === 'location' ? 'locations' : 'equipment';
  };
  ctrl.refreshNavRenderData = function () {
    var locationOrder = { Site: 0, Building: 1, Level: 2, Room: 3 };
    function naturalCompare(aStr, bStr) {
      var a = (aStr || '').toString();
      var b = (bStr || '').toString();
      if (a.localeCompare) {
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      }
      a = a.toLowerCase();
      b = b.toLowerCase();
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    }
    function sortRecords(list) {
      return list.slice().sort(function (a, b) {
        var aIsLoc = ctrl.isLocationClass(a['@class']);
        var bIsLoc = ctrl.isLocationClass(b['@class']);
        if (aIsLoc && !bIsLoc) return -1;
        if (!aIsLoc && bIsLoc) return 1;
        if (aIsLoc && bIsLoc) {
          var aRank = locationOrder[a['@class']] !== undefined ? locationOrder[a['@class']] : Number.MAX_SAFE_INTEGER;
          var bRank = locationOrder[b['@class']] !== undefined ? locationOrder[b['@class']] : Number.MAX_SAFE_INTEGER;
          if (aRank !== bRank) return aRank - bRank;
          var aIndex = typeof a.index === 'number' ? a.index : null;
          var bIndex = typeof b.index === 'number' ? b.index : null;
          if (aIndex !== null && bIndex !== null && aIndex !== bIndex) return aIndex - bIndex;
        }
        var aName = a.name || a['@class'] || '';
        var bName = b.name || b['@class'] || '';
        return naturalCompare(aName, bName);
      });
    }
    const currentChildren = (ctrl.currentNode && ctrl.currentNode.children) || [];
    const records = currentChildren
      .filter(function (child) { return !ctrl.isExcluded(child); })
      .filter(function (child) {
        if (ctrl.selectionType() !== 'location') return true;
        return ctrl.isLocationClass(child['@class']) && child['@class'] !== 'Dashboard';
      });
    ctrl.navRenderData.length = 0;
    ctrl.navRenderData.push.apply(ctrl.navRenderData, sortRecords(records));
    ctrl.reinitializeNavPagination(true);
  };

  ctrl.actualLevel = prop('Home');
  ctrl.ancestors = prop([{ '@class': 'Home', name: 'Home' }]);

  ctrl.isLocationClass = function (cls) {
    return ['Home', 'Site', 'Building', 'Level', 'Room', 'Dashboard'].includes(cls);
  };

  ctrl.isSelectableLocationClass = function (cls) {
    return ['Site', 'Building', 'Level', 'Room'].includes(cls);
  };

  ctrl.isSelectable = function (node) {
    if (!node || !node['@rid'] || ctrl.isExcluded(node)) return false;
    if (ctrl.selectionType() === 'location') {
      return ctrl.isSelectableLocationClass(node['@class']);
    }
    return !ctrl.isLocationClass(node['@class']);
  };

  ctrl.isExcluded = function (node) {
    if (!node || !node['@rid']) return false;
    var attrs = ctrl.attrs || {};
    var excluded = [];
    if (Array.isArray(attrs.excludedRids)) excluded = excluded.concat(attrs.excludedRids);
    if (attrs.excludedRid) excluded.push(attrs.excludedRid);
    return excluded.some(function (rid) { return rid === node['@rid']; });
  };

  ctrl.normalizeRid = function (rid) {
    const value = String(rid || '').trim();
    if (!value) return '';
    return value.charAt(0) === '#' ? value : '#' + value;
  };

  ctrl.pathLabelForNode = function (node) {
    if (!node || !node['@rid']) return '';
    if (node._pathLabel) return node._pathLabel;

    const chain = [];
    let currentRid = node['@rid'];
    while (currentRid) {
      const currentNode = ctrl.nodeMap.get(currentRid);
      if (currentNode) chain.push(currentNode);
      currentRid = ctrl.parentMap.get(currentRid);
    }
    const labels = [{ name: 'Home', '@class': 'Home' }].concat(chain.reverse())
      .map(function (record) {
        if (!record) return '';
        if (record.name === 'root' || record['@class'] === 'Home') return 'Home';
        return record.roomNumber
          ? ((record.name || record['@class'] || '') + ' ' + record.roomNumber).trim()
          : (record.name || record['@class'] || '');
      })
      .filter(Boolean);
    return labels.join(' / ');
  };

  ctrl.applyInitialRid = function () {
    const initialRid = ctrl.normalizeRid(ctrl.attrs && ctrl.attrs.initialRid);
    if (!initialRid || ctrl.appliedInitialRid === initialRid || !ctrl.treeRoot) return;
    const alternateRid = initialRid.charAt(0) === '#' ? initialRid.slice(1) : '#' + initialRid;
    const node = ctrl.nodeMap.get(initialRid) || ctrl.nodeMap.get(alternateRid);
    ctrl.appliedInitialRid = initialRid;
    if (!node || ctrl.isExcluded(node)) return;
    ctrl.resetSearchState();
    ctrl.setCurrentNode(node);
  };

  ctrl.resetSearchState = function (keepActive) {
    ctrl.searchTerm('');
    ctrl.searchActive(!!keepActive);
    ctrl.searchResults([]);
    ctrl.searchError('');
    ctrl.searching(false);
    ctrl.searchRequestId++;
    ctrl.reinitializePagination(true);
  };

  ctrl.buildIndex = function (node, parentRid) {
    if (!node || !node.rid) return;
    node['@rid'] = node['@rid'] || node.rid;
    node['@class'] = node['@class'] || node.class;
    ctrl.nodeMap.set(node.rid, node);
    if (parentRid) ctrl.parentMap.set(node.rid, parentRid);
    if (Array.isArray(node.children)) {
      node.children.forEach(function (child) { ctrl.buildIndex(child, node.rid); });
    }
  };

  ctrl.loadTree = function () {
    if (ctrl.treePromise) return ctrl.treePromise;
    ctrl.treePromise = pickerRequestState.run(ctrl, function () {
      return loaders.requestWithLoader({
        loader: 'main',
        url: '/api/v1/location/descendant-tree',
        method: 'GET'
      })
        .then(function (res) {
        ctrl.treeRoot = res && res.root;
        ctrl.rootRid = ctrl.treeRoot && ctrl.treeRoot.rid ? ctrl.treeRoot.rid : null;
        ctrl.nodeMap = new Map();
        ctrl.parentMap = new Map();
        ctrl.buildIndex(ctrl.treeRoot, null);
        const flat = Array.isArray(res && res.flat) ? res.flat : [];
        const equipment = flat
          .filter(function (entry) { return entry && entry.class && !ctrl.isLocationClass(entry.class); })
          .map(function (entry) {
            return {
              rid: entry.rid,
              name: entry.name || entry.class || 'Equipment',
              class: entry.class || 'Equipment',
              path: entry.path || ''
            };
          });
        const locations = flat
          .filter(function (entry) { return entry && ctrl.isSelectableLocationClass(entry.class); })
          .map(function (entry) {
            return {
              rid: entry.rid,
              name: entry.name || entry.class || 'Location',
              class: entry.class || 'Location',
              path: entry.path || ''
            };
          });
        ctrl.descendantSearchData = { equipment: equipment, locations: locations, flat: flat, root: ctrl.treeRoot };
        ctrl.setCurrentNode(ctrl.treeRoot);
        ctrl.startView = true;
        ctrl.applyInitialRid();
        m.redraw();
        return ctrl.descendantSearchData;
        });
    }, {
      onRetry: ctrl.getHome,
      errorMessage: 'Unable to load descendant tree:'
    })
      .then(function (result) {
        ctrl.treePromise = null;
        return result;
      });
    return ctrl.treePromise;
  };

  ctrl.handleGlobalSearch = function (rawQuery, opts) {
    var keepActive = !!(opts && opts.keepActive);
    var inputValue = (rawQuery || '').toString();
    const query = inputValue.trim();
    ctrl.searchTerm(inputValue);
    if (!query) {
      ctrl.resetSearchState(keepActive);
      return;
    }
    const token = ++ctrl.searchRequestId;
    ctrl.searching(true);
    ctrl.searchError('');
    const lowerQuery = query.toLowerCase();
    function naturalCompare(aStr, bStr) {
      var aVal = (aStr == null ? '' : aStr).toString();
      var bVal = (bStr == null ? '' : bStr).toString();
      if (aVal && aVal.localeCompare) return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
      aStr = aVal; // keep compatibility with existing comparisons below
      bStr = bVal;
      if (aStr < bStr) return -1;
      if (aStr > bStr) return 1;
      return 0;
    }
    function scoreEntry(entry) {
      function lower(val) {
        return val == null ? '' : String(val).toLowerCase();
      }
      var name = lower(entry.name);
      var cls = lower(entry.class);
      var path = lower(entry.path);
      function fieldScore(val, weight) {
        if (!val) return Number.MAX_SAFE_INTEGER;
        var idx = val.indexOf(lowerQuery);
        if (idx === -1) return Number.MAX_SAFE_INTEGER;
        return idx + weight;
      }
      var bestName = fieldScore(name, 0);
      var bestClass = fieldScore(cls, 10);
      var bestPath = fieldScore(path, 20);
      return Math.min(bestName, bestClass, bestPath);
    }
    return ctrl.loadTree()
      .then(function () {
        if (token !== ctrl.searchRequestId) return;
        const dataKey = ctrl.selectionType() === 'location' ? 'locations' : 'equipment';
        const searchList = Array.isArray(ctrl.descendantSearchData && ctrl.descendantSearchData[dataKey]) ? ctrl.descendantSearchData[dataKey] : [];
        const matches = searchList
          .filter(function (entry) { return entry && entry.rid; })
          .filter(function (entry) { return !ctrl.isExcluded({ '@rid': entry.rid }); })
          .filter(function (entry) {
            return [entry.name, entry.class, entry.path].some(function (val) {
              return typeof val === 'string' && val.toLowerCase().includes(lowerQuery);
            });
          })
          .map(function (entry) {
            var score = scoreEntry(entry);
            return {
              '@rid': entry.rid,
              '@class': entry.class || (ctrl.selectionType() === 'location' ? 'Location' : 'Equipment'),
              name: entry.name || entry.class || (ctrl.selectionType() === 'location' ? 'Location' : 'Equipment'),
              _pathLabel: entry.path || '',
              _score: score
            };
          })
          .sort(function (a, b) {
            if (a._score !== b._score) return a._score - b._score;
            return naturalCompare(a.name || a['@class'], b.name || b['@class']);
          });
        ctrl.searchResults(matches);
        ctrl.searching(false);
        ctrl.searchRenderData = JSON.parse(JSON.stringify(matches));
        ctrl.reinitializePagination(true);
        return matches;
      })
      .catch(function (err) {
        if (token !== ctrl.searchRequestId) return;
        console.error('Global ' + ctrl.selectionName() + ' search failed', err);
        ctrl.searchResults([]);
        ctrl.searching(false);
        ctrl.searchError('Unable to search ' + ctrl.selectionPluralName() + ' right now.');
        return [];
      });
  };

  ctrl.updateAncestorsFromRid = function (rid) {
    const chain = [];
    let currentRid = rid;
    while (currentRid) {
      const node = ctrl.nodeMap.get(currentRid);
      if (node) chain.push(node);
      currentRid = ctrl.parentMap.get(currentRid);
    }
    ctrl.ancestors([{ '@class': 'Home', name: 'Home' }].concat(chain.reverse()));
  };

  ctrl.setCurrentNode = function (node) {
    if (!node) return;
    ctrl.currentNode = node;
    ctrl.actualLevel(node['@class'] || node.class || 'Home');
    ctrl.updateAncestorsFromRid(node.rid);
    ctrl.selectedNode('');
    ctrl.refreshNavRenderData();
  };

  ctrl.getHome = function () {
    if (ctrl.treeRoot) {
      ctrl.resetSearchState();
      ctrl.setCurrentNode(ctrl.treeRoot);
      return Promise.resolve(ctrl.treeRoot);
    }
    return ctrl.loadTree();
  };

  ctrl.navigateLocation = function (loc) {
    if (!loc || !loc['@rid']) return;
    const node = ctrl.nodeMap.get(loc['@rid']);
    if (node) {
      ctrl.resetSearchState();
      ctrl.setCurrentNode(node);
    }
  };

  ctrl.navigateEquipment = function (equipment) {
    // For equipment, allow drilling down via hasComponent; still only equipment selectable
    const node = ctrl.nodeMap.get(equipment['@rid']);
    if (node) {
      ctrl.resetSearchState();
      ctrl.setCurrentNode(node);
    }
  };

  ctrl.selectNode = function (node) {
    if (!ctrl.isSelectable(node)) return;
    node._pathLabel = ctrl.pathLabelForNode(node);
    ctrl.selectedNode(node);
    if (ctrl.attrs && typeof ctrl.attrs.onSelect === 'function') {
      try {
        var maybeResult = ctrl.attrs.onSelect(node);
        if (maybeResult && typeof maybeResult.then === 'function') {
          maybeResult
            .then(function (resolved) {
              if (resolved) ctrl.selectedNode(resolved);
            })
            .catch(function (err) {
              console.error('treeEquipment onSelect handler failed', err);
            });
        } else if (maybeResult) {
          ctrl.selectedNode(maybeResult);
        }
      } catch (err) {
        console.error('treeEquipment onSelect handler error', err);
      }
    }
  };

  ctrl.getHome();
};

treeEquipment.onbeforeupdate = function (vnode) {
  this.attrs = vnode.attrs || {};
  this.selectedNode = vnode.attrs.selectedNode;
  if (this.startView && typeof this.applyInitialRid === 'function') {
    this.applyInitialRid();
  }
  return true;
};

treeEquipment.view = function (vnode) {
  var ctrl = this;
  var stateView = pickerRequestState.view(ctrl);
  if (stateView) return stateView;
  if (!ctrl.startView) {
    return m('div.flex.justify-center.items-center.pa4', [
      m('img', {
        src: '/images/loading-bars.svg',
        style: { width: '64px', height: '64px' },
        alt: 'Loading equipment tree'
      })
    ]);
  }

  function breadcrumb() {
    const trail = Array.isArray(ctrl.ancestors()) ? ctrl.ancestors() : [];
    const home = trail[0] || { '@class': 'Home', name: 'Home' };
    const rest = trail
      .slice(1)
      .filter(function (ancestor) { return ancestor && ancestor.rid !== ctrl.rootRid; });
    return m('div.mb2.pb2.bb.bw1.b--mid-gray.near-white', [
      m('div.dib-ns.mr2.pointer', {
        class: (home['@class'] === ctrl.actualLevel()) ? 'active' : 'blue',
        onclick: ctrl.getHome
      }, ['Home', ' / ']),
      rest.map(function (ancestor) {
        var label = (ancestor.name === 'root') ? 'Home' : (ancestor.name || ancestor['@class']);
        return m('div.dib-ns.mr2.pointer', {
          class: ancestor['@class'] === ctrl.actualLevel() ? 'active' : 'blue',
          onclick: function () {
            if (ctrl.isLocationClass(ancestor['@class'])) return ctrl.navigateLocation(ancestor);
            ctrl.navigateEquipment(ancestor);
          }
        }, [label, ' / ']);
      })
    ]);
  }

  function searchBar() {
    var active = ctrl.searchActive();
    return m('div.mb2.flex.items-end', [
      m(active ? 'span.w-100.ba.b--blue.br2.ph1.flex.items-center.h2' : 'span', {
        id: ctrl.searchWrapperId,
        onclick: function () {
          if (!active) {
            ctrl.searchActive(true);
            setTimeout(function () {
              var el = document.getElementById(ctrl.searchInputId);
              if (el) el.focus();
            }, 1);
          }
        }
      }, [
        m('div.nowrap', { style: { display: active ? 'none' : 'block' } }, [
          m('i', { class: 'blue f2 ion-search pointer' })
        ]),
        m('div.flex.items-center.w-100', { style: { display: active ? 'flex' : 'none' } }, [
          m('input.mh1.mv1.f4.smarttable-search-input', {
            id: ctrl.searchInputId,
            class: 'tree-equipment-search-input',
            placeholder: 'Search all ' + ctrl.selectionPluralName(),
            value: ctrl.searchTerm(),
            oninput: function (ev) { ctrl.handleGlobalSearch(ev.target.value, { keepActive: true }); },
            onblur: function () {
              if (!ctrl.searchTerm()) ctrl.searchActive(false);
            }
          }),
          ctrl.searchTerm() && ctrl.searchTerm().length > 0
            ? m('i', {
              class: 'blue f4 mr1 ion-close-circled pointer',
              onclick: function () {
                ctrl.handleGlobalSearch('');
                ctrl.searchActive(false);
              }
            })
            : null
        ])
      ])
    ]);
  }

  function renderList(records, fromGlobalSearch) {
    if (!records || !records.length) return null;
    return records.map(function (record) {
      if (!record || !record['@rid'] || record['@class'] == 'Dashboard') return;
      const isLoc = ctrl.isLocationClass(record['@class']);
      const isSelectable = ctrl.isSelectable(record);
      const showPath = fromGlobalSearch && record._pathLabel;
      const primaryLabel = showPath ? record._pathLabel : (record.name || record['@class']);
      const canExpand = !!(
        !fromGlobalSearch &&
        Array.isArray(record.children) &&
        record.children.length &&
        (isLoc || ctrl.selectionType() !== 'location')
      );
      return m(verticalListItem, {
        label: m('span', [
          primaryLabel,
          canExpand ? m('i', { class: 'ion-ios-arrow-right ml2 f3 b' }) : ''
        ]),
        labelClass: fromGlobalSearch ? '' : isLoc || (Array.isArray(record.children) && record.children.length) ? 'dark-blue' : '',
        onclickLabel: function () {
          if (fromGlobalSearch) return ctrl.selectNode(record);
          if (isLoc && canExpand) return ctrl.navigateLocation(record);
          if (Array.isArray(record.children) && record.children.length) return ctrl.navigateEquipment(record);
          ctrl.selectNode(record);
        }
      }, [
        isSelectable ? m('i', {
          onclick: function () { ctrl.selectNode(record); },
          class: 'fr f3 b ' + (ctrl.selectedNode() && ctrl.selectedNode()['@rid'] == record['@rid'] ? 'ion-ios-circle-filled' : 'ion-ios-circle-outline')
        }) : null
      ]);
    });
  }

  // Compose view: include locations at Home, child locations at other levels, and equipment
  const useSearch = !!ctrl.searchTerm();
  return m('div.mb2', [
    m('label', 'Select ' + ctrl.selectionName() + ':'),
    m('br'),
    breadcrumb(),
    searchBar(),
    ctrl.searchTerm() && ctrl.searchError() ? m('div.red.mt1', ctrl.searchError()) : null,
    ctrl.searchTerm() && ctrl.searching() ? m('div.gray.mt1', 'Searching across all locations...') : null,
    ctrl.searchTerm() && !ctrl.searching() && !ctrl.searchError() && ctrl.searchResults().length === 0
      ? m('div.mid-gray.mt1', 'No ' + ctrl.selectionPluralName() + ' found across the tree')
      : null,
    useSearch ? [
      renderList(ctrl.searchRenderData, true),
      m('div.mt3'),

        m(pagination, {
          data: ctrl.searchRenderData,
          itemsPerPage: 10,
          reinitializePagination: ctrl.reinitializePagination
        })
    ] : [
      renderList(ctrl.navRenderData, false),
      m('div.mt3', [
        m(pagination, {
          data: ctrl.navRenderData,
          itemsPerPage: 10,
          reinitializePagination: ctrl.reinitializeNavPagination
        })
      ])
    ]
  ]);
};

module.exports = treeEquipment;
